import { track } from '../lib/track';
import { captureCart } from '../lib/cartLead';
import { useAuth } from './AuthContext';
import { createContext, useContext, useEffect, useRef, useState, useMemo } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'shinyvault-cart';
// When the cart last changed. Drives the "still waiting" banner for returning
// visitors we have no email for.
export const TOUCHED_KEY = 'shinyvault-cart-touched';

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// The cart lives client-side (localStorage). For a signed-in shopper it is
// also mirrored to sv_cart_leads so a left-behind cart can be emailed about
// (lib/cartLead.js). Guests are mirrored from the checkout page once they
// type an email. Orders/order_items are still only written at checkout.
export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const { user } = useAuth();
  const firstRun = useRef(true);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Signed-in mirror, debounced so quantity taps don't each hit the server.
  // An empty cart on first load says nothing about the shopper's intent (new
  // device), so it doesn't close a lead; an emptied cart later does.
  useEffect(() => {
    const initial = firstRun.current;
    firstRun.current = false;
    if (!user) return undefined;
    if (initial && items.length === 0) return undefined;
    const t = setTimeout(() => captureCart(null, items, 'account'), 1500);
    return () => clearTimeout(t);
  }, [items, user]);

  const touch = () => {
    try { localStorage.setItem(TOUCHED_KEY, String(Date.now())); } catch { /* private mode */ }
  };

  const addItem = (product, quantity = 1) => {
    track('add_to_cart', {
      label: product?.name ?? null,
      product_id: product?.id ?? null,
      value_cents: (product?.price_cents ?? 0) * quantity,
      meta: { quantity },
    });
    touch();
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          slug: product.slug,
          price_cents: product.price_cents,
          image_url: product.image_url || null,
          quantity,
        },
      ];
    });
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) return removeItem(productId);
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity } : i)));
  };

  const clearCart = () => setItems([]);

  // Swap in a cart rebuilt from an email link (CartPage handles ?restore=).
  const replaceItems = (next) => { touch(); setItems(next); };

  const subtotalCents = useMemo(
    () => items.reduce((sum, i) => sum + i.price_cents * i.quantity, 0),
    [items]
  );
  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clearCart, replaceItems, subtotalCents, itemCount }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
